import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Switch,
  Space,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import apiClient from '../services/api';
import dayjs from 'dayjs';

const { TextArea } = Input;

const Offers = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/offers', { params: { page, per_page: pageSize } });
      const d = res.data;
      setData(d.data || []);
      setPagination((prev) => ({
        ...prev,
        current: d.current_page || page,
        total: d.total || 0,
      }));
    } catch {
      message.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      ...record,
      starts_at: record.starts_at ? dayjs(record.starts_at) : null,
      ends_at: record.ends_at ? dayjs(record.ends_at) : null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      starts_at: values.starts_at?.toISOString(),
      ends_at: values.ends_at?.toISOString(),
    };
    try {
      if (editing) {
        await apiClient.put(`/admin/offers/${editing.id}`, payload);
        message.success('Offer updated');
      } else {
        await apiClient.post('/admin/offers', payload);
        message.success('Offer created');
      }
      setModalOpen(false);
      fetchData(pagination.current);
    } catch {
      message.error('Failed to save offer');
    }
  };

  const handleToggle = async (id) => {
    try {
      await apiClient.patch(`/admin/offers/${id}/toggle-status`);
      fetchData(pagination.current);
    } catch {
      message.error('Failed to toggle offer');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/admin/offers/${id}`);
      message.success('Offer deleted');
      fetchData(pagination.current);
    } catch {
      message.error('Failed to delete offer');
    }
  };

  const columns = [
    { title: 'Title', dataIndex: 'title', key: 'title' },
    {
      title: 'Badge',
      dataIndex: 'badge_text',
      key: 'badge_text',
      render: (v) => (v ? <Tag color="red">{v}</Tag> : null),
    },
    {
      title: 'Discount',
      key: 'discount',
      render: (_, r) =>
        `${r.discount_type === 'percentage' ? r.discount_value + '%' : '$' + r.discount_value}`,
    },
    {
      title: 'Product',
      key: 'product',
      render: (_, r) => r.product?.name || r.category?.name || '-',
    },
    {
      title: 'Active',
      key: 'is_active',
      render: (_, r) => <Switch checked={r.is_active} onChange={() => handleToggle(r.id)} />,
    },
    {
      title: 'Valid',
      key: 'dates',
      render: (_, r) => {
        if (r.starts_at && r.ends_at)
          return `${r.starts_at.slice(0, 10)} ~ ${r.ends_at.slice(0, 10)}`;
        if (r.starts_at) return `From ${r.starts_at.slice(0, 10)}`;
        if (r.ends_at) return `Until ${r.ends_at.slice(0, 10)}`;
        return 'Always';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete this offer?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Special Offers"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Offer
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (t) => `Total ${t} offers`,
        }}
        onChange={(p) => fetchData(p.current, p.pageSize)}
      />
      <Modal
        title={editing ? 'Edit Offer' : 'Add Offer'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} />
          </Form.Item>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="discount_type" label="Discount Type" rules={[{ required: true }]}>
              <Select style={{ width: 140 }}>
                <Select.Option value="percentage">Percentage</Select.Option>
                <Select.Option value="fixed">Fixed</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="discount_value" label="Discount Value" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: 140 }} />
            </Form.Item>
          </Space>
          <Form.Item name="badge_text" label="Badge Text">
            <Input placeholder="e.g. SALE, NEW, BESTSELLER" />
          </Form.Item>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="starts_at" label="Starts At">
              <DatePicker showTime />
            </Form.Item>
            <Form.Item name="ends_at" label="Ends At">
              <DatePicker showTime />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Card>
  );
};

export default Offers;
